// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useNavigate } from 'react-router-dom';
import type { ClientDetailDTO } from '../../types/clientList';
import { LOATableCard, EyeIcon } from '../loa';
import type { LOATableColumn, LOATableAction } from '../loa/types';

type ClientLinkedRelationshipsTabProps = Readonly<{
  client: ClientDetailDTO;
}>;

interface LinkedRelationship {
  id: string;
  relationshipName: string;
  relationshipType: string;
  crmRelationshipNumber?: string;
  fullRelationshipName: string;
}

const columns: LOATableColumn<LinkedRelationship>[] = [
  {
    key: 'relationshipName',
    label: 'Relationship name',
    render: (row) => <span style={{ fontWeight: 500 }}>{row.relationshipName}</span>,
  },
  {
    key: 'relationshipType',
    label: 'Relationship type',
    render: (row) => row.relationshipType,
  },
  {
    key: 'crmRelationshipNumber',
    label: 'CRM relationship no.',
    render: (row) => row.crmRelationshipNumber || '-',
  },
];

export function ClientLinkedRelationshipsTab({ client }: ClientLinkedRelationshipsTabProps) {
  const navigate = useNavigate();

  const linkedRelationships: LinkedRelationship[] = client.profileId ? [
    {
      id: client.profileId,
      relationshipName: `${client.firstName} ${client.lastName}`,
      relationshipType: 'Joint',
      crmRelationshipNumber: client.crmPersonNumber || undefined,
      fullRelationshipName: client.profileName || `${client.firstName} ${client.lastName}`,
    }
  ] : [];

  const actions: LOATableAction<LinkedRelationship>[] = [
    {
      key: 'view',
      icon: <EyeIcon />,
      label: 'View relationship details',
      onClick: (row) => navigate(`/relationships/${row.id}`),
    },
  ];

  return (
    <LOATableCard
      data={linkedRelationships}
      columns={columns}
      actions={actions}
      keyExtractor={(r) => r.id}
      title="Linked Relationships"
      emptyMessage="This client is not linked to any relationships yet."
    />
  );
}
